import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CompanyFilter } from '../components/CompanyFilter';
import { useDateRange } from '../hooks/useDateRange';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Download, TrendingDown } from 'lucide-react';

export default function SavingsPage() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterSite, setFilterSite] = useState('all');
  const { dateFrom, dateTo, setDateFrom, setDateTo, dateParams } = useDateRange();

  const fetchData = () => {
    const params = {};
    if (filterCompany !== 'all') params.company_id = filterCompany;
    if (filterSite !== 'all') params.site_id = filterSite;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get(`/analytics/savings?${new URLSearchParams(params)}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get('/companies').then(r => setCompanies(r.data)).catch(() => {});
    api.get('/sites').then(r => setSites(r.data)).catch(() => {});
    fetchData();
  }, []);
  useEffect(() => { setLoading(true); fetchData(); }, [filterCompany, filterSite, dateFrom, dateTo]);

  const chartData = (data.opportunities || []).slice(0, 8).map(o => ({ name: o.current_product?.substring(0, 12), extra: o.total_extra_spend }));

  const downloadReport = async () => {
    const res = await api.get('/reports/generate/savings', { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = 'savings_report.xlsx'; a.click();
  };

  return (
    <div data-testid="savings-page">
      <div className="page-header">
        <div><h1 className="page-title">Savings Opportunities</h1><p className="text-sm text-slate-500 mt-1">Identify cost leakage and product switch opportunities</p></div>
        <Button variant="outline" onClick={downloadReport} data-testid="download-savings-report"><Download size={14} className="mr-1.5" />Export Report</Button>
      </div>

      <div className="flex gap-3 mb-6 flex-wrap items-end" data-testid="savings-filters">
        <CompanyFilter companies={companies} value={filterCompany} onChange={setFilterCompany} />
        <Select value={filterSite} onValueChange={setFilterSite}><SelectTrigger className="w-48 bg-white"><SelectValue placeholder="All Sites" /></SelectTrigger><SelectContent><SelectItem value="all">All Sites</SelectItem>{sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}</SelectContent></Select>
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} prefix="savings" />
      </div>

      {loading ? <div className="animate-pulse h-48 bg-slate-100 rounded-lg" /> : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            <Card className="stat-card" data-testid="kpi-total-spend"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Spend</p><p className="text-2xl font-bold text-slate-900 mt-1 font-heading">₹{(data.total_spend || 0).toLocaleString()}</p></Card>
            <Card className="stat-card border-orange-200 bg-orange-50/30" data-testid="kpi-savings-potential"><p className="text-xs font-semibold uppercase tracking-wider text-orange-600">Savings Potential</p><p className="text-2xl font-bold text-orange-700 mt-1 font-heading">₹{(data.total_savings_potential || 0).toLocaleString()}</p></Card>
            <Card className="stat-card border-emerald-200 bg-emerald-50/30" data-testid="kpi-annual-savings"><p className="text-xs font-semibold uppercase tracking-wider text-emerald-600">Projected Annual Savings</p><p className="text-2xl font-bold text-emerald-700 mt-1 font-heading">₹{(data.projected_annual_savings || 0).toLocaleString()}</p></Card>
            <Card className="stat-card"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hidden Cost Leakage</p><p className="text-2xl font-bold text-red-600 mt-1 font-heading">₹{(data.hidden_cost_leakage || 0).toLocaleString()}</p>{data.data_status && <span className={`text-[10px] uppercase tracking-wider mt-1 inline-block px-1.5 py-0.5 rounded ${data.data_status === 'actual' ? 'bg-emerald-50 text-emerald-600' : data.data_status === 'estimated' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-400'}`}>{data.data_status}</span>}</Card>
          </div>

          {data.best_product && (
            <Card className="bg-emerald-50 border border-emerald-200 rounded-lg p-5 mb-6">
              <div className="flex items-center gap-3"><TrendingDown size={20} className="text-emerald-600" /><div><p className="text-sm font-semibold text-emerald-900">Best Performing Product: {data.best_product}</p><p className="text-xs text-emerald-700">Lowest cost per hour at ₹{data.best_product_cph?.toFixed(2)}/hr</p></div></div>
            </Card>
          )}

          {/* Chart */}
          {chartData.length > 0 && (
            <Card className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
              <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Extra Spend by Product (vs Best)</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartData} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis type="number" tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={v => `₹${v.toLocaleString()}`} /><YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11, fill: '#334155' }} /><Tooltip formatter={v => `₹${Number(v).toLocaleString()}`} contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12 }} /><Bar dataKey="extra" fill="#EA580C" radius={[0,4,4,0]} name="Extra Spend" /></BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Opportunities Table */}
          <Card className="bg-white border border-slate-200 rounded-lg">
            <div className="px-5 py-3 border-b border-slate-100"><h3 className="text-sm font-semibold text-slate-900 font-heading">Switch Opportunities</h3></div>
            <div className="data-table-wrapper border-0 shadow-none rounded-none">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Current Product</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Current ₹/Hr</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Recommended</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Rec. ₹/Hr</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Extra ₹/Hr</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Extra Spend</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hours</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Confidence</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(data.opportunities || []).length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">No savings opportunities identified yet</TableCell></TableRow> :
                  (data.opportunities || []).map((o, i) => (
                    <TableRow key={i} className="hover:bg-slate-50/50">
                      <TableCell className="font-medium text-slate-900">{o.current_product}</TableCell>
                      <TableCell className="text-sm text-red-600 font-medium">₹{o.current_cph?.toFixed(2)}</TableCell>
                      <TableCell className="font-medium text-emerald-700">{o.recommended_product}</TableCell>
                      <TableCell className="text-sm text-emerald-600 font-medium">₹{o.recommended_cph?.toFixed(2)}</TableCell>
                      <TableCell className="text-sm font-bold text-orange-600">₹{o.extra_spend_per_hour?.toFixed(2)}</TableCell>
                      <TableCell className="text-sm font-bold text-red-600">₹{o.total_extra_spend?.toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-slate-600">{o.hours_recorded}h</TableCell>
                      <TableCell><span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${o.confidence === 'high' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>{o.confidence}</span></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
